// ProjectTreeNode — one row in the Worksnip left-panel tree.
//
// Visual contract (reference: Pre-Sales Visualizer ENGAGEMENTS panel,
// screenshot 2026-05-21_21h54_19.png):
//
//   ┌─ 16px drag handle (6 dots, hover-only) ────────────────────────────┐
//   │ ┌─ 24px number badge (count of items, or "All" glyph) ───────────┐ │
//   │ │ ┌─ 8px gap, then 2 lines of text ────────────────────────────┐ │ │
//   │ │ │ Label  (text-sm font-medium)                                │ │ │
//   │ │ │ Subtitle (text-xs text-ws-ink-3) — N captures · 3d ago      │ │ │
//   │ │ └─────────────────────────────────────────────────────────────┘ │ │
//   │ └──────────────────────────────────────── kebab on hover (right) ─┘ │
//   └────────────────────────────────────────────────────────────────────┘
//   Row height ≈ 52px. Active row: 3px accent left border + soft fill.
//   Indent: 16px per depth level + a 1px dashed vertical guide per level.
//
// What's load-bearing here:
//   • The HTML5 dnd wiring is unchanged from the previous revision — same
//     edge bisection (25/50/25), same `application/x-worksnip` payload.
//     The visual chrome is layered ON TOP of the existing draggable div.
//   • `draggable` lives on the OUTER row (so the entire row, including the
//     handle, is the drag source). The visible 6-dot handle is purely
//     affordance — there's no `onMouseDown` capture or pointer-events trickery.
//   • Selection still flips via onClick on the row; the kebab + chevron call
//     stopPropagation so they don't double-fire.
//
// Drop edges (folders accept all three, leaves only before/after):
//   top 25%    → 'before'
//   bottom 25% → 'after'
//   middle 50% → 'inside'

import { useState } from 'react';
import {
  ChevronRight, ChevronDown, Film, MoreVertical,
  LayoutGrid, Plus, Trash2, Pencil, ExternalLink, FolderClosed, Wrench,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { GuideLeaf, ProjectNode } from '../../types/project';
import styles from './ProjectTreeRow.module.css';
import { InlineRename } from '../ui/InlineRename';

export type DropEdge = 'before' | 'after' | 'inside' | null;

// ── Shared row geometry ────────────────────────────────────────────────────

/** Left padding for a row at a given depth.
 *  Layout:  16px (drag handle gutter)  +  depth * 16px (indent)  +  6px (breathing) */
function leftPadFor(depth: number): number {
  return 16 + depth * 16 + 6;
}

/** Sprint-15 — sleek connector lines.
 *  Renders N solid 1px vertical guides (one per ancestor depth level) PLUS
 *  a horizontal elbow at this row's depth so each row reads as a proper
 *  tree node. When `isLastSibling` is true the outermost vertical line
 *  terminates at the row mid-line so the elbow closes as └─ instead of ├─.
 */
function IndentGuides({ depth, isLastSibling }: { depth: number; isLastSibling?: boolean }) {
  // W1 (Sprint-19) — emit a VIRTUAL-ROOT vertical guide for depth-0 rows
  // too. The old early-return left the root siblings (Inbox / All captures
  // / first-level folders) floating with no spine to connect them. Now
  // every row gets at least one vertical line on the left edge so the
  // whole tree visually reads as one connected hierarchy.
  const guides: React.ReactNode[] = [];
  const total = depth + 1; // include the depth-0 spine
  for (let i = 0; i < total; i += 1) {
    // Each guide sits centered in its 16px indent column. Depth-0 sits at
    // left:8 (the virtual root column), depth-1 at left:24, etc.
    const left = 8 + i * 16;
    // Only the OUTERMOST (deepest) line terminates as └─ when this row
    // is the last sibling — ancestors keep going past this row.
    const terminate = isLastSibling && i === total - 1;
    guides.push(
      <span
        key={`v${i}`}
        aria-hidden
        className={`${styles.indentGuide}${terminate ? ' ' + styles.indentGuideLast : ''}`}
        style={{ left }}
      />,
    );
  }
  // Horizontal elbow at the current row's depth — extends from the deepest
  // vertical guide rightward into the chevron column. Widened 8 → 12 so
  // the connector visibly reaches the folder/leaf icon.
  const elbowLeft  = 8 + depth * 16;
  const elbowWidth = 12;
  guides.push(
    <span
      key="elbow"
      aria-hidden
      className={styles.indentElbow}
      style={{ left: elbowLeft, width: elbowWidth }}
    />,
  );
  return <>{guides}</>;
}

/** Six-dot grab handle — visible on hover, sits in the 16px gutter. */
function DragHandle({ visible }: { visible: boolean }) {
  return (
    <span
      aria-hidden
      className={`absolute top-1/2 -translate-y-1/2 left-1 grid place-items-center transition-opacity ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ width: 12, height: 20, cursor: 'grab' }}
    >
      <span className={styles.dragDots} />
    </span>
  );
}

/** Circle badge — folders show their item count, leaves show a film glyph
 *  via the `icon` slot (number is hidden then). */
function NumBadge({
  count,
  active,
  variant = 'idle',
  icon,
  ariaLabel,
}: {
  count?: number;
  active?: boolean;
  variant?: 'idle' | 'all';
  icon?: React.ReactNode;
  ariaLabel?: string;
}) {
  const cls =
    active ? styles.numBadgeActive
    : variant === 'all' ? styles.numBadgeAll
    : styles.numBadgeIdle;
  return (
    <span className={`${styles.numBadge} ${cls}`} aria-label={ariaLabel}>
      {icon ?? count ?? 0}
    </span>
  );
}

/** Tiny human-friendly relative time. Falls back to ISO if parsing fails. */
function relTime(iso: string | undefined, t: TFunction): string {
  if (!iso) return '';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const diff = (Date.now() - ts) / 1000;
  if (diff < 60)             return t('common:relativeTime.justNow');
  if (diff < 3600)           return t('common:relativeTime.minutesAgo', { count: Math.floor(diff / 60) });
  if (diff < 86400)          return t('common:relativeTime.hoursAgo', { count: Math.floor(diff / 3600) });
  if (diff < 86400 * 7)      return t('common:relativeTime.daysAgo', { count: Math.floor(diff / 86400) });
  if (diff < 86400 * 30)     return t('tree.node.weeksAgo', { count: Math.floor(diff / 86400 / 7) });
  if (diff < 86400 * 365)    return t('tree.node.monthsAgo', { count: Math.floor(diff / 86400 / 30) });
  return t('tree.node.yearsAgo', { count: Math.floor(diff / 86400 / 365) });
}

// ── Project (folder) row ───────────────────────────────────────────────────

export interface ProjectRowProps {
  node: ProjectNode;
  /** W9-7 — outline-style numbering "1", "1.1" etc. Computed in ProjectTree. */
  displayNumber: string;
  /** True if either the node has sub-projects OR has guides parked under it. */
  hasChildren: boolean;
  /** Total descendant item count to render in the badge — projects + guides. */
  itemCount: number;
  /** Latest activity timestamp inside the folder, ISO. Optional. */
  latestActivity?: string;
  isExpanded: boolean;
  isSelected: boolean;
  isDragging: boolean;
  dropEdge: DropEdge;
  isRenaming: boolean;

  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onStartRename: (id: string) => void;
  onCommitRename: (id: string, next: string) => void;
  onCancelRename: () => void;
  onAddChild: (parentId: string) => void;
  onDelete: (id: string) => void;

  onDragStart: (kind: 'project', id: string) => void;
  onDragEnd: () => void;
  onDragOverRow: (id: string, kind: 'project', edge: 'before' | 'after' | 'inside') => void;
  onDropRow: (id: string, kind: 'project', edge: 'before' | 'after' | 'inside') => void;
}

export function ProjectRow({
  node, displayNumber, hasChildren, itemCount, latestActivity,
  isExpanded, isSelected, isDragging, dropEdge, isRenaming,
  onSelect, onToggle, onStartRename, onCommitRename, onCancelRename,
  onAddChild, onDelete,
  onDragStart, onDragEnd, onDragOverRow, onDropRow,
}: ProjectRowProps) {
  const { t } = useTranslation('projects');
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const paddingLeft = leftPadFor(node.depth);

  // Subtitle: "N captures · 3d ago" — the secondary line that turns the row
  // into a true two-line summary like the reference.
  const captureCountLabel = t('tree.node.captureCount', { count: itemCount });
  const subtitleBits: string[] = [];
  subtitleBits.push(captureCountLabel);
  const rel = relTime(latestActivity, t);
  if (rel) subtitleBits.push(rel);
  const subtitle = subtitleBits.join(' · ');

  const rowBg = isSelected
    ? 'bg-ws-accent-quiet'
    : 'hover:bg-ws-surface-2';

  const textCls = isSelected
    ? 'text-ws-accent-ink'
    : 'text-ws-ink-2 group-hover:text-ws-ink';

  return (
    <div
      role="treeitem"
      aria-selected={isSelected}
      aria-expanded={hasChildren ? isExpanded : undefined}
      aria-level={node.depth + 1}
      draggable
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        const payload = JSON.stringify({ type: 'project', id: node.id });
        e.dataTransfer.setData('application/x-worksnip', payload);
        e.dataTransfer.setData('text/plain', node.id);
        onDragStart('project', node.id);
      }}
      onDragEnd={() => onDragEnd()}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const r = e.currentTarget.getBoundingClientRect();
        const offset = e.clientY - r.top;
        const edge: 'before' | 'after' | 'inside' =
          offset < r.height * 0.25 ? 'before'
          : offset > r.height * 0.75 ? 'after'
          : 'inside';
        onDragOverRow(node.id, 'project', edge);
      }}
      onDrop={(e) => {
        e.preventDefault();
        const r = e.currentTarget.getBoundingClientRect();
        const offset = e.clientY - r.top;
        const edge: 'before' | 'after' | 'inside' =
          offset < r.height * 0.25 ? 'before'
          : offset > r.height * 0.75 ? 'after'
          : 'inside';
        onDropRow(node.id, 'project', edge);
      }}
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('button, input')) return;
        if (isRenaming) return;
        onSelect(node.id);
      }}
      className={`group relative flex items-center gap-1.5 pr-1 cursor-pointer select-none ${rowBg}`}
      style={{
        paddingLeft,
        // W9-7 — compact rows mirror the Visualizer SubModuleTreeEditor: a
        // dense outline reads better in a 260px rail than the old 52px
        // capture-card style. Was 52 → now 30. Padding stays so connector
        // lines have room to land.
        minHeight: 30,
        opacity: isDragging ? 0.4 : 1,
        boxShadow:
          dropEdge === 'before' ? 'inset 0 2px 0 0 var(--ws-accent)'
          : dropEdge === 'after' ? 'inset 0 -2px 0 0 var(--ws-accent)'
          : isSelected ? 'inset 3px 0 0 0 var(--ws-accent)'
          : 'none',
        outline: dropEdge === 'inside' ? '2px solid var(--ws-accent)' : 'none',
        outlineOffset: dropEdge === 'inside' ? -2 : 0,
        transition:
          'background-color var(--ws-duration-fast) var(--ws-ease), ' +
          'color var(--ws-duration-fast) var(--ws-ease)',
      }}
    >
      {/* Sleek tree connector — solid vertical hairlines + elbow. */}
      <IndentGuides depth={node.depth} isLastSibling={node.isLastSibling} />

      {/* Drag handle — appears on hover only. */}
      <DragHandle visible={hovered && !isRenaming} />

      {/* Chevron — 14px slot. Hidden when the folder has no children so the
          rail line doesn't get a stale arrow. */}
      <span className="flex-shrink-0 inline-flex items-center justify-center" style={{ width: 14 }}>
        {hasChildren && (
          <button
            type="button"
            aria-label={isExpanded ? t('tree.view.collapse') : t('tree.view.expand')}
            className="w-4 h-4 inline-flex items-center justify-center text-ws-ink-3 hover:text-ws-ink rounded-sm"
            onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
          >
            {isExpanded
              ? <ChevronDown size={11} strokeWidth={2} />
              : <ChevronRight size={11} strokeWidth={2} />}
          </button>
        )}
      </span>

      {/* W9-7 — section-square anchor (Visualizer pattern). A small 10×10
          accent-tinted square reads as the section marker without dominating
          the row the way the 20px circle badge did. */}
      <span
        aria-hidden
        className="flex-shrink-0"
        style={{
          width: 10,
          height: 10,
          borderRadius: 2,
          background: isSelected ? 'var(--ws-accent)' : 'color-mix(in srgb, var(--ws-accent) 60%, transparent)',
        }}
      />

      {/* Number prefix + label on a single line (Visualizer outline style). */}
      <div className="flex-1 min-w-0 flex items-center gap-2 py-1">
        <span
          className="flex-shrink-0 font-mono tabular-nums"
          style={{
            color: 'var(--ws-ink-3)',
            fontSize: 10,
            fontWeight: 600,
            minWidth: 18,
          }}
        >
          {displayNumber}
        </span>
        {isRenaming ? (
          <InlineRename
            initial={node.name}
            onCommit={(v) => onCommitRename(node.id, v)}
            onCancel={onCancelRename}
          />
        ) : (
          <span
            className="truncate"
            title={node.name}
            onDoubleClick={(e) => { e.stopPropagation(); onStartRename(node.id); }}
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: '0.2px',
              textTransform: 'uppercase',
              lineHeight: 1.3,
              color: isSelected ? 'var(--ws-accent-ink)' : 'var(--ws-ink)',
            }}
          >
            {node.name}
          </span>
        )}
        {/* Right-aligned compact count — only when >0 so empty folders don't
            broadcast their emptiness. Reads as "12" not "12 captures" to
            keep the row tight. */}
        {!isRenaming && itemCount > 0 && (
          <span
            className="ml-auto flex-shrink-0 font-mono tabular-nums"
            style={{
              color: isSelected ? 'var(--ws-accent-ink)' : 'var(--ws-ink-3)',
              fontSize: 10,
              fontWeight: 500,
              opacity: 0.85,
            }}
            aria-label={rel ? t('tree.node.captureCountWithActivity', { count: itemCount, activity: rel }) : captureCountLabel}
            title={subtitle || t('tree.node.emptyFolder')}
          >
            {itemCount}
          </span>
        )}
      </div>

      {/* Hover actions — explicit Trash2 + kebab. The Trash is a deliberate
          shortcut (one-click to archive) so the user doesn't have to open
          the menu for the most common destructive action; the kebab still
          carries Rename / New sub-folder / Archive for completeness. */}
      <span
        className={`self-center flex-shrink-0 flex items-center gap-0.5 pr-0.5 ${
          menuOpen || hovered ? 'opacity-100' : 'opacity-0'
        } transition-opacity`}
      >
        <button
          type="button"
          aria-label={t('tree.node.deleteFolderAria', { name: node.name })}
          title={t('tree.node.deleteFolderTitle')}
          className="w-6 h-6 inline-flex items-center justify-center rounded-full text-ws-ink-3 hover:text-ws-danger hover:bg-ws-surface"
          onClick={(e) => { e.stopPropagation(); onDelete(node.id); }}
        >
          <Trash2 size={13} strokeWidth={1.75} />
        </button>
        <button
          type="button"
          aria-label={t('tree.node.folderActionsAria')}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className="w-6 h-6 inline-flex items-center justify-center rounded-full text-ws-ink-3 hover:text-ws-ink hover:bg-ws-surface"
          onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
        >
          <MoreVertical size={14} strokeWidth={2} />
        </button>
      </span>

      {menuOpen && (
        <RowMenu
          publicId={node.public_id}
          onClose={() => setMenuOpen(false)}
          items={[
            { icon: <Pencil size={12} strokeWidth={1.75} />, label: t('tree.node.rename'),        onClick: () => { setMenuOpen(false); onStartRename(node.id); } },
            { icon: <Plus size={12} strokeWidth={1.75} />,   label: t('tree.node.newSubfolder'),  onClick: () => { setMenuOpen(false); onAddChild(node.id); } },
            { icon: <Trash2 size={12} strokeWidth={1.75} />, label: t('tree.node.archive'),       onClick: () => { setMenuOpen(false); onDelete(node.id); }, danger: true },
          ]}
        />
      )}
    </div>
  );
}

// ── Guide leaf row ─────────────────────────────────────────────────────────

export interface GuideRowProps {
  leaf: GuideLeaf;
  /** W9-7 — outline-style numbering, e.g. "1.2.3". */
  displayNumber: string;
  isDragging: boolean;
  dropEdge: DropEdge;
  isActive: boolean;
  /** Single-click selects the row (visual), double-click opens the studio. */
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
  onRename: (id: string) => void;
  onDelete: (id: string) => void;

  onDragStart: (kind: 'guide', id: string) => void;
  onDragEnd: () => void;
  /** Guide leaves only accept 'before' / 'after' — no 'inside'. */
  onDragOverRow: (id: string, kind: 'guide', edge: 'before' | 'after') => void;
  onDropRow: (id: string, kind: 'guide', edge: 'before' | 'after') => void;
}

export function GuideRow({
  leaf, displayNumber, isDragging, dropEdge, isActive,
  onSelect, onOpen, onRename, onDelete,
  onDragStart, onDragEnd, onDragOverRow, onDropRow,
}: GuideRowProps) {
  const { t } = useTranslation('projects');
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const paddingLeft = leftPadFor(leaf.depth);

  const rowBg = isActive ? 'bg-ws-accent-quiet' : 'hover:bg-ws-surface-2';
  const titleCls = isActive
    ? 'text-ws-accent-ink'
    : 'text-ws-ink group-hover:text-ws-ink';
  const subCls = isActive ? 'text-ws-accent-ink' : 'text-ws-ink-3';

  const subtitle = leaf.public_id || t('tree.node.unfiledCapture');
  const captured = relTime(leaf.created_at, t);

  return (
    <div
      role="treeitem"
      aria-level={leaf.depth + 1}
      draggable
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        const payload = JSON.stringify({ type: 'guide', id: leaf.id });
        e.dataTransfer.setData('application/x-worksnip', payload);
        e.dataTransfer.setData('text/plain', leaf.id);
        onDragStart('guide', leaf.id);
      }}
      onDragEnd={() => onDragEnd()}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const r = e.currentTarget.getBoundingClientRect();
        const edge: 'before' | 'after' =
          e.clientY < r.top + r.height / 2 ? 'before' : 'after';
        onDragOverRow(leaf.id, 'guide', edge);
      }}
      onDrop={(e) => {
        e.preventDefault();
        const r = e.currentTarget.getBoundingClientRect();
        const edge: 'before' | 'after' =
          e.clientY < r.top + r.height / 2 ? 'before' : 'after';
        onDropRow(leaf.id, 'guide', edge);
      }}
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('button, input')) return;
        onSelect(leaf.id);
      }}
      onDoubleClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('button, input')) return;
        onOpen(leaf.id);
      }}
      className={`group relative flex items-center gap-1.5 pr-1 cursor-pointer select-none ${rowBg}`}
      style={{
        paddingLeft,
        // W9-7 — match folder-row sizing (was 52). A single-line leaf row.
        minHeight: 28,
        opacity: isDragging ? 0.4 : 1,
        boxShadow:
          dropEdge === 'before' ? 'inset 0 2px 0 0 var(--ws-accent)'
          : dropEdge === 'after' ? 'inset 0 -2px 0 0 var(--ws-accent)'
          : isActive ? 'inset 3px 0 0 0 var(--ws-accent)'
          : 'none',
        transition:
          'background-color var(--ws-duration-fast) var(--ws-ease), ' +
          'color var(--ws-duration-fast) var(--ws-ease)',
      }}
      title={`${leaf.title || t('tree.node.untitledCapture')}${leaf.public_id ? ' · ' + leaf.public_id : ''}${captured ? ' · ' + captured : ''}`}
    >
      <IndentGuides depth={leaf.depth} isLastSibling={leaf.isLastSibling} />
      <DragHandle visible={hovered} />

      {/* W9-7 — leaf marker: small Film glyph in muted ink instead of the
          old 20px filled circle. Sits where folders show their section
          square, so vertical alignment across folder + leaf rows stays
          tight. */}
      <span
        className="flex-shrink-0 inline-flex items-center justify-center"
        style={{ width: 14, color: isActive ? 'var(--ws-accent)' : 'var(--ws-ink-4)' }}
        aria-hidden
      >
        <Film size={11} strokeWidth={1.75} />
      </span>

      <div className="flex-1 min-w-0 flex items-center gap-2 py-1">
        <span
          className="flex-shrink-0 font-mono tabular-nums"
          style={{
            color: 'var(--ws-ink-3)',
            fontSize: 10,
            fontWeight: 500,
            minWidth: 26,
          }}
        >
          {displayNumber}
        </span>
        <span
          className={`truncate ${titleCls}`}
          style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.3 }}
        >
          {leaf.title || <span className="italic text-ws-ink-3">{t('tree.node.untitledWorksnip')}</span>}
        </span>
        {leaf.processing_status && leaf.processing_status !== 'ready' && leaf.processing_status !== 'error' && (
          <span className={`ml-auto flex-shrink-0 italic ${subCls}`} style={{ fontSize: 10 }}>
            {t('tree.node.processing')}
          </span>
        )}
      </div>

      <span
        className={`self-center flex-shrink-0 flex items-center gap-0.5 pr-0.5 ${
          menuOpen || hovered ? 'opacity-100' : 'opacity-0'
        } transition-opacity`}
      >
        {/* Explicit one-click delete next to the kebab — same affordance the
            library cards expose, so the user has the same destructive shortcut
            in every surface (tree row, dashboard grid card, project detail
            card). The kebab still carries Open / Rename / Delete for
            completeness + accessibility. */}
        <button
          type="button"
          aria-label={t('tree.node.deleteWorksnipAria', { title: leaf.title || '' })}
          title={t('tree.node.deleteWorksnipTitle')}
          className="w-6 h-6 inline-flex items-center justify-center rounded-full text-ws-ink-3 hover:text-ws-danger hover:bg-ws-surface"
          onClick={(e) => { e.stopPropagation(); onDelete(leaf.id); }}
        >
          <Trash2 size={13} strokeWidth={1.75} />
        </button>
        <button
          type="button"
          aria-label={t('tree.node.worksnipActionsAria')}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className="w-6 h-6 inline-flex items-center justify-center rounded-full text-ws-ink-3 hover:text-ws-ink hover:bg-ws-surface"
          onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
        >
          <MoreVertical size={14} strokeWidth={2} />
        </button>
      </span>

      {menuOpen && (
        <RowMenu
          publicId={leaf.public_id}
          onClose={() => setMenuOpen(false)}
          items={[
            { icon: <ExternalLink size={12} strokeWidth={1.75} />, label: t('tree.node.open'),   onClick: () => { setMenuOpen(false); onOpen(leaf.id); } },
            { icon: <Pencil size={12} strokeWidth={1.75} />,       label: t('tree.node.rename'), onClick: () => { setMenuOpen(false); onRename(leaf.id); } },
            { icon: <Trash2 size={12} strokeWidth={1.75} />,       label: t('common:actions.delete'), onClick: () => { setMenuOpen(false); onDelete(leaf.id); }, danger: true },
          ]}
        />
      )}
    </div>
  );
}

// ── "All captures" row ─────────────────────────────────────────────────────
//
// Sits above the tree. Always rendered (even when the tree is empty) so the
// "see everything" affordance is one click away. Doubles as a drop catcher
// for "drag a guide here to unfile it" — handled by the parent ProjectTree.

export interface AllCapturesRowProps {
  total: number;
  isActive: boolean;
  onSelect: () => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
}

export function AllCapturesRow({
  total, isActive, onSelect, onDragOver, onDrop,
}: AllCapturesRowProps) {
  const { t } = useTranslation('projects');
  return (
    <button
      type="button"
      onClick={onSelect}
      onDragOver={onDragOver}
      onDrop={onDrop}
      // W9-7 — compact 30px pinned row matches the rest of the tree on
      // desktop; mobile (< lg, drawer/touch) grows to the 44px target size.
      className={`group w-full text-left relative flex items-center gap-1.5 pr-2 cursor-pointer select-none min-h-[30px] max-lg:min-h-[44px] ${
        isActive ? 'bg-ws-accent-quiet' : 'hover:bg-ws-surface-2'
      }`}
      style={{
        paddingLeft: leftPadFor(0),
        boxShadow: isActive ? 'inset 3px 0 0 0 var(--ws-accent)' : 'none',
        transition:
          'background-color var(--ws-duration-fast) var(--ws-ease), ' +
          'color var(--ws-duration-fast) var(--ws-ease)',
      }}
    >
      <span className="flex-shrink-0 inline-flex items-center justify-center"
            style={{ width: 14, color: isActive ? 'var(--ws-accent)' : 'var(--ws-ink-3)' }}
            aria-hidden>
        <LayoutGrid size={12} strokeWidth={1.75} />
      </span>
      <span
        className="flex-1 truncate"
        style={{
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: '0.2px',
          textTransform: 'uppercase',
          color: isActive ? 'var(--ws-accent-ink)' : 'var(--ws-ink-2)',
          lineHeight: 1.3,
        }}
      >
        {t('tree.node.allItems')}
      </span>
      <span
        className="flex-shrink-0 font-mono tabular-nums"
        style={{ fontSize: 10, color: isActive ? 'var(--ws-accent-ink)' : 'var(--ws-ink-3)' }}
        aria-label={t('tree.node.captureCount', { count: total })}
      >
        {total}
      </span>
    </button>
  );
}

// ── "__inbox" (virtual) row ────────────────────────────────────────────────
//
// A pseudo-folder pinned above All-captures that holds every guide with no
// project_id — i.e. the default landing zone for a fresh capture. It is NOT
// backed by a Project row in the DB; the key `'__inbox'` is a UI sentinel.
//
// Visual diffs vs a real folder row:
//   • italicized label + a quiet "System" eyebrow so the row reads as
//     "this isn't user-created"
//   • Inbox icon in the badge slot (different from the All-captures
//     LayoutGrid glyph and from real folders' counted badge)
//   • not draggable / not renamable / no kebab menu
//
// Accepts drops:
//   • drop a guide here → guideTreeApi.setProject(id, null)  (un-file it)

export interface InboxRowProps {
  count: number;
  isActive: boolean;
  onSelect: () => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
}

export function InboxRow({
  count, isActive, onSelect, onDragOver, onDrop,
}: InboxRowProps) {
  const { t } = useTranslation('projects');
  return (
    <button
      type="button"
      onClick={onSelect}
      onDragOver={onDragOver}
      onDrop={onDrop}
      // W9-7 — compact 30px pinned row matches the rest of the tree on
      // desktop; mobile (< lg, drawer/touch) grows to the 44px target size.
      className={`group w-full text-left relative flex items-center gap-1.5 pr-2 cursor-pointer select-none min-h-[30px] max-lg:min-h-[44px] ${
        isActive ? 'bg-ws-accent-quiet' : 'hover:bg-ws-surface-2'
      }`}
      style={{
        paddingLeft: leftPadFor(0),
        boxShadow: isActive ? 'inset 3px 0 0 0 var(--ws-accent)' : 'none',
        transition:
          'background-color var(--ws-duration-fast) var(--ws-ease), ' +
          'color var(--ws-duration-fast) var(--ws-ease)',
      }}
      aria-label={t('tree.node.workbenchAria', { count })}
    >
      <span className="flex-shrink-0 inline-flex items-center justify-center"
            style={{ width: 14, color: isActive ? 'var(--ws-accent)' : 'var(--ws-ink-3)' }}
            aria-hidden>
        {/* W9-9 — Wrench instead of Inbox. The bucket is now called
            "Workbench" (workspace metaphor: snips land on your bench, then
            get filed into project drawers). The old Inbox name + icon
            clashed with the notifications inbox at /inbox. */}
        <Wrench size={12} strokeWidth={1.75} />
      </span>

      {/* W9-9 — Label + SYSTEM pill on the left, count on the right.
          Tooltip carries the drag-to-file hint so the row stays compact. */}
      <span className="flex-1 min-w-0 flex items-center gap-2"
            title={count === 0
              ? t('tree.node.workbenchEmptyHint')
              : t('tree.node.workbenchUnfiledHint', { count })}>
        <span
          className={`truncate italic ${isActive ? 'text-ws-accent-ink' : 'text-ws-ink-2'}`}
          style={{
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: '0.2px',
            textTransform: 'uppercase',
            lineHeight: 1.3,
          }}
        >
          {t('tree.node.workbench')}
        </span>
        <span
          className="flex-shrink-0 px-1 py-px text-[8.5px] uppercase tracking-wider font-semibold rounded-sm"
          style={{
            background: 'var(--ws-surface-2)',
            color: 'var(--ws-ink-4)',
            border: '1px solid var(--ws-border)',
          }}
        >
          {t('tree.node.system')}
        </span>
      </span>
      {count > 0 && (
        <span
          className="flex-shrink-0 font-mono tabular-nums"
          style={{ fontSize: 10, color: isActive ? 'var(--ws-accent-ink)' : 'var(--ws-ink-3)' }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// ── shared bits ────────────────────────────────────────────────────────────

interface RowMenuItem {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}

interface RowMenuProps {
  publicId?: string;
  items: RowMenuItem[];
  onClose: () => void;
}

function RowMenu({ publicId, items, onClose }: RowMenuProps) {
  return (
    <>
      {/* Click-outside catcher */}
      <div
        className="fixed inset-0 z-40"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
      />
      <div
        role="menu"
        className="absolute right-1 top-12 z-50 min-w-[180px] rounded-md border border-ws-border bg-ws-surface p-1 ws-anim-fade-up"
        style={{ boxShadow: 'var(--ws-shadow)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {publicId && (
          <>
            <div className="px-2 py-1.5 text-[10px] font-mono text-ws-ink-3 select-text">
              {publicId}
            </div>
            <div className="h-px bg-ws-border my-1" />
          </>
        )}
        {items.map((it) => (
          <button
            key={it.label}
            type="button"
            role="menuitem"
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm hover:bg-ws-surface-2 ${
              it.danger ? 'text-ws-danger' : 'text-ws-ink-2 hover:text-ws-ink'
            }`}
            onClick={it.onClick}
          >
            {it.icon}
            {it.label}
          </button>
        ))}
      </div>
    </>
  );
}
